import React, { useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Box,
  Checkbox,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Collapse,
  IconButton,
  Chip,
  Avatar,
  useTheme,
  alpha,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PersonIcon from "@mui/icons-material/Person";

/**
 * FundUserSelectionTable component for selecting funds and users with improved design
 *
 * @param {Object} props
 * @param {Array} props.funds - List of funds with their users
 * @param {boolean} props.loading - Loading state indicator
 * @param {function} props.onSelectionChange - Callback when selection changes, receives array of selected user IDs
 */
interface User {
  id: string;
  name: string;
  sub: string;
  role: string;
}

interface Fund {
  id: string;
  name: string;
  users: User[];
}

export default function FundUserSelectionTable({
  funds = [] as Fund[],
  loading = false,
  onSelectionChange = (selectedUsers: string[]) => {},
}) {
  const theme = useTheme();

  // State for processed data
  const [data, setData] = useState<{
    funds: Fund[];
    allUsers: User[];
  }>({
    funds: [],
    allUsers: [],
  });

  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [openFunds, setOpenFunds] = useState<Record<string, boolean>>({});

  // Process incoming funds prop
  useEffect(() => {
    if (funds && funds.length > 0) {
      setData((prevData) => ({
        ...prevData,
        funds: funds,
      }));

      // Initialize all funds as open
      const initialOpenState: Record<string, boolean> = {};
      funds.forEach((fund: Fund) => {
        if (fund && fund.id) {
          initialOpenState[fund.id] = false;
        }
      });
      setOpenFunds(initialOpenState);
    }
  }, [funds]);

  // Process data to get a unique list of all users
  useEffect(() => {
    if (data.funds && data.funds.length > 0) {
      const usersMap: Record<string, User> = {};
      data.funds.forEach((fund: Fund) => {
        if (fund && fund.users) {
          (fund as Fund).users.forEach((user) => {
            if (user && user.id) {
              usersMap[user.id] = user;
            }
          });
        }
      });
      setData((prev) => ({
        ...prev,
        allUsers: Object.values(usersMap),
      }));
    }
  }, [data.funds]);

  // Notify parent component when selection changes
  useEffect(() => {
    onSelectionChange(selectedUsers);
  }, [selectedUsers, onSelectionChange]);

  const handleTabChange = (
    _event: any,
    newValue: React.SetStateAction<number>
  ) => {
    setTabValue(newValue);
  };

  const toggleFundOpen = (fundId: string) => {
    setOpenFunds((prev) => ({
      ...prev,
      [fundId]: !prev[fundId],
    }));
  };

  // Check if a fund is fully selected
  const isFundSelected = (fundId: string) => {
    const fund = data.funds.find((f) => f && f.id === fundId);
    if (!fund || !fund.users || fund.users.length === 0) return false;

    return fund.users.every((user) => user && selectedUsers.includes(user.sub));
  };

  // Handle fund selection/deselection
  const toggleFund = (fundId: string) => {
    const fund = data.funds.find((f) => f && f.id === fundId);
    if (!fund || !fund.users) return;

    const isCurrentlySelected = isFundSelected(fundId);
    let newSelectedUsers = [...selectedUsers];

    if (isCurrentlySelected) {
      // Deselect all users from this fund
      newSelectedUsers = newSelectedUsers.filter(
        (userSub) => !fund.users.some((user) => user && user.sub === userSub)
      );
    } else {
      // Select all users from this fund
      fund.users.forEach((user) => {
        if (user && user.sub && !newSelectedUsers.includes(user.sub)) {
          newSelectedUsers.push(user.sub);
        }
      });
    }

    setSelectedUsers(newSelectedUsers);
  };

  // Handle individual user selection/deselection
  const toggleUser = (userSub: string) => {
    if (selectedUsers.includes(userSub)) {
      setSelectedUsers(selectedUsers.filter((sub) => sub !== userSub));
    } else {
      setSelectedUsers([...selectedUsers, userSub]);
    }
  };

  const handleSelectionChange = (userSubs: string[] | any[]) => {
    setSelectedUsers(Array.isArray(userSubs) ? userSubs : []);

    // Update selectedUserSub with all selected user subs
    if (Array.isArray(userSubs) && userSubs.length > 0) {
      const selectedSubs: string[] = [];
      for (const userId of userSubs) {
        for (const fund of data.funds) {
          const fundAccountMember = fund.users?.find((u) => u.sub === userId);
          if (fundAccountMember) {
            selectedSubs.push(fundAccountMember.sub);
          }
        }
      }
      console.log("Selected user subs:", selectedSubs.join(", ")); // Log selected subs
    } else {
      console.log("Selected user subs: None");
    }
  };

  // Get fund names for a user (for the All Users tab)
  const getUserFunds = (userSub: string) => {
    if (!data.funds) return [];

    const fundNames = data.funds
      .filter(
        (fund) => fund.users && fund.users.some((user) => user.sub === userSub)
      )
      .map((fund) => fund.name);

    return fundNames;
  };

  // Generate initials from name
  const getInitials = (name: string) => {
    if (!name) return "?";
    const nameParts = name.split(" ");
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          centered
          sx={{
            "& .MuiTab-root": {
              minHeight: 48,
              fontSize: "0.875rem",
              fontWeight: 500,
            },
            "& .Mui-selected": {
              fontWeight: 600,
            },
          }}
        >
          <Tab
            label="By Funds"
            icon={<AccountBalanceWalletIcon fontSize="small" />}
            iconPosition="start"
          />
          <Tab
            label="By Users"
            icon={<PersonIcon fontSize="small" />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Funds & Users Tab */}
      <Box role="tabpanel" hidden={tabValue !== 0}>
        {tabValue === 0 && (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  }}
                >
                  <TableCell padding="checkbox" sx={{ pl: 2 }}></TableCell>
                  <TableCell
                    sx={{ py: 1.5, fontSize: "0.75rem", fontWeight: 600 }}
                  >
                    Select Fund
                  </TableCell>
                  <TableCell
                    sx={{ py: 1.5, fontSize: "0.75rem", fontWeight: 600 }}
                  ></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.funds && data.funds.length > 0 ? (
                  data.funds
                    .map(
                      (fund) =>
                        fund && [
                          // Fund row
                          <TableRow
                            key={`fund-${fund.id}`}
                            sx={{
                              bgcolor: alpha(
                                theme.palette.background.default,
                                0.5
                              ),
                              "&:hover": {
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.04
                                ),
                              },
                            }}
                          >
                            <TableCell padding="checkbox" sx={{ pl: 2 }}>
                              <Checkbox
                                checked={isFundSelected(fund.id)}
                                onChange={() => toggleFund(fund.id)}
                                size="small"
                                color="primary"
                              />
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <Box display="flex" alignItems="center">
                                <IconButton
                                  aria-label="expand row"
                                  size="small"
                                  onClick={() => toggleFundOpen(fund.id)}
                                  sx={{ mr: 0.5 }}
                                >
                                  {openFunds[fund.id] ? (
                                    <KeyboardArrowUpIcon fontSize="small" />
                                  ) : (
                                    <KeyboardArrowDownIcon fontSize="small" />
                                  )}
                                </IconButton>
                                <Box display="flex" flexDirection="column">
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    sx={{ lineHeight: 1.4 }}
                                  >
                                    {fund.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: "0.7rem" }}
                                  >
                                    {fund.users?.length || 0} member
                                    {fund.users?.length !== 1 ? "s" : ""}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {fund.id}
                              </Typography>
                            </TableCell>
                          </TableRow>,
                          // Users rows
                          <TableRow key={`fund-users-${fund.id}`}>
                            <TableCell
                              style={{ paddingBottom: 0, paddingTop: 0 }}
                              colSpan={3}
                            >
                              <Collapse
                                in={openFunds[fund.id]}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box sx={{ my: 1 }}>
                                  <Table size="small" aria-label="users">
                                    <TableBody>
                                      {fund.users &&
                                        fund.users.map(
                                          (user) =>
                                            user && (
                                              <TableRow
                                                key={`user-${fund.id}-${user.sub}`}
                                                sx={{
                                                  "&:hover": {
                                                    bgcolor: alpha(
                                                      theme.palette.primary
                                                        .main,
                                                      0.04
                                                    ),
                                                  },
                                                  borderBottom: `1px solid ${alpha(
                                                    theme.palette.divider,
                                                    0.3
                                                  )}`,
                                                }}
                                              >
                                                <TableCell
                                                  padding="checkbox"
                                                  sx={{ pl: 6 }}
                                                >
                                                  <Checkbox
                                                    checked={selectedUsers.includes(
                                                      user.sub
                                                    )}
                                                    onChange={() =>
                                                      toggleUser(user.sub)
                                                    }
                                                    size="small"
                                                    color="primary"
                                                  />
                                                </TableCell>
                                                <TableCell
                                                  component="th"
                                                  scope="row"
                                                  sx={{ py: 1 }}
                                                >
                                                  <Box
                                                    display="flex"
                                                    alignItems="center"
                                                  >
                                                    <Avatar
                                                      sx={{
                                                        width: 28,
                                                        height: 28,
                                                        mr: 1.5,
                                                        fontSize: "0.75rem",
                                                        bgcolor:
                                                          theme.palette.primary
                                                            .main,
                                                      }}
                                                    >
                                                      {getInitials(user.name)}
                                                    </Avatar>
                                                    <Box>
                                                      <Typography
                                                        variant="body2"
                                                        sx={{
                                                          fontWeight: 500,
                                                          lineHeight: 1.3,
                                                        }}
                                                      >
                                                        {user.name}
                                                      </Typography>
                                                    </Box>
                                                  </Box>
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{ py: 1 }}
                                                >
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                  >
                                                    {user.role}
                                                  </Typography>
                                                </TableCell>
                                              </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>,
                        ]
                    )
                    .flat()
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary" variant="body2">
                        No funds available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* All Users Tab */}
      <Box role="tabpanel" hidden={tabValue !== 1}>
        {tabValue === 1 && (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  }}
                >
                  <TableCell padding="checkbox" sx={{ pl: 2 }}></TableCell>
                  <TableCell
                    sx={{ py: 1.5, fontSize: "0.75rem", fontWeight: 600 }}
                  >
                    User
                  </TableCell>
                  <TableCell
                    sx={{ py: 1.5, fontSize: "0.75rem", fontWeight: 600 }}
                  ></TableCell>
                  <TableCell
                    sx={{ py: 1.5, fontSize: "0.75rem", fontWeight: 600 }}
                  >
                    Fund Membership
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.allUsers && data.allUsers.length > 0 ? (
                  data.allUsers.map(
                    (user) =>
                      user && (
                        <TableRow
                          key={`all-users-${user.sub}`}
                          sx={{
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                            },
                            borderBottom: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          <TableCell padding="checkbox" sx={{ pl: 2 }}>
                            <Checkbox
                              checked={selectedUsers.includes(user.sub)}
                              onChange={() => {
                                toggleUser(user.sub);
                                handleSelectionChange([
                                  ...selectedUsers,
                                  user.sub,
                                ]);
                              }}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ py: 1.5 }}
                          >
                            <Box display="flex" alignItems="center">
                              <Avatar
                                sx={{
                                  width: 28,
                                  height: 28,
                                  mr: 1.5,
                                  fontSize: "0.75rem",
                                  bgcolor: theme.palette.primary.main,
                                }}
                              >
                                {getInitials(user.name)}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, lineHeight: 1.3 }}
                                >
                                  {user.name}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}></TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Box>
                              {getUserFunds(user.sub).map((fundName, idx) => (
                                <Chip
                                  key={idx}
                                  label={fundName}
                                  size="small"
                                  sx={{
                                    mr: 0.5,
                                    mb: 0.5,
                                    height: 22,
                                    fontSize: "0.7rem",
                                    bgcolor: alpha(
                                      theme.palette.secondary.main,
                                      0.1
                                    ),
                                    color: theme.palette.secondary.dark,
                                  }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary" variant="body2">
                        No users available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box
        mt={2}
        px={1}
        py={1}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          borderRadius: 1,
          bgcolor:
            selectedUsers.length > 0
              ? alpha(theme.palette.primary.main, 0.08)
              : "transparent",
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, fontSize: "0.8rem" }}
        >
          {selectedUsers.length > 0 ? (
            <>
              Selected: <strong>{selectedUsers.length}</strong> user
              {selectedUsers.length !== 1 ? "s" : ""}
            </>
          ) : (
            <>No users selected</>
          )}
        </Typography>

        {selectedUsers.length > 0 && (
          <Typography
            variant="body2"
            color="primary"
            sx={{
              cursor: "pointer",
              fontSize: "0.8rem",
              fontWeight: 500,
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => setSelectedUsers([])}
          >
            Clear all
          </Typography>
        )}
      </Box>
    </Box>
  );
}
